// @tier: SHARED — accessible Basic + Premium (historique de trajets). Le statut individuel
// de chaque élève (trip_attendance.status) n'est affiché qu'en Premium (student_checkin) :
// Basic n'a pas d'app chauffeur pour confirmer réellement la présence, donc pas de statut fiable
// à montrer — seule la liste des élèves de l'arrêt reste affichée. Exception : le check
// parentConfirmedAt (tap du parent sur son écran de suivi live) s'affiche dans les DEUX tiers —
// contrairement à `status`, il ne dépend d'aucune app chauffeur, donc tout aussi fiable en Basic.
import { useState } from 'react';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Clock, MapPin, ArrowLeft, Route, Timer, TrendingUp, ChevronDown, ChevronUp } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';

import useHistory, { calculateDuration } from '../hooks/trHistory';
import { useFeatureFlags } from '../hooks/useFeatureFlags';
import TripReplayMap from '../components/TripReplayMap';
import ParentConfirmedTick from '../components/ParentConfirmedTick';

const getStatusBadge = (status) => {
  switch (status) {
    case 'completed':
      return <Badge className="bg-green-100 text-green-800 hover:bg-green-200 border-green-200">Terminé</Badge>;
    case 'active':
    case 'in_progress':
      return <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-200 border-blue-200">En cours</Badge>;
    case 'scheduled':
      return (
        <Badge variant="outline" className="text-gray-500">
          Prévu
        </Badge>
      );
    default:
      return <Badge variant="secondary">{status}</Badge>;
  }
};

// Un trip force-terminé (bouton superadmin ou sweep minuit, voir
// docs/ideas/force-trip-ended.md) — null/absent = fin normale, pas de badge.
const END_REASON_LABELS = {
  force_end_manual: 'Forcé (manuel)',
  force_end_sweep: 'Forcé (minuit)',
};

const getEndReasonBadge = (endReason) => {
  const label = END_REASON_LABELS[endReason];
  if (!label) return null;
  return (
    <Badge variant="outline" className="border-orange-300 bg-orange-50 text-orange-700">
      {label}
    </Badge>
  );
};

const StatCard = ({ icon: Icon, label, value, color = 'text-gray-900' }) => (
  <div className="flex flex-col items-center gap-1 rounded-lg bg-gray-50 px-4 py-3 text-center">
    <Icon className={`h-4 w-4 ${color}`} />
    <span className={`text-lg font-bold leading-none ${color}`}>{value}</span>
    <span className="text-xs text-gray-500">{label}</span>
  </div>
);

function SectionToggle({ label, open, onToggle }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="flex w-full items-center justify-between border-b border-gray-100 bg-gray-50/60 px-6 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 hover:bg-gray-100/60 transition-colors"
    >
      <span>{label}</span>
      {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
    </button>
  );
}

function getStopTime(stop) {
  const raw =
    stop.markedAt ?? stop.actualDeparture ?? stop.actualArrival ?? stop.recorded_at ?? stop.completed_at ?? null;
  if (!raw) return null;
  try {
    const d = new Date(raw);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return null;
  }
}

function getStopStatusColor(status) {
  if (status === 'completed' || status === 'visited') return '#22c55e';
  if (status === 'skipped') return '#94a3b8';
  return '#f97316';
}

function getStopStatusLabel(status) {
  if (status === 'completed' || status === 'visited') return 'Complété';
  if (status === 'skipped') return 'Passé';
  return 'En attente';
}

// Statut individuel de l'élève (trip_attendance.status) — indépendant du statut de l'arrêt :
// un arrêt "Complété" peut contenir un élève marqué absent ce jour-là.
function getStudentStatusColor(status) {
  if (status === 'present') return '#22c55e';
  if (status === 'absent') return '#ef4444';
  if (status === 'skipped') return '#94a3b8';
  return '#f97316'; // pending
}

function getStudentStatusLabel(status) {
  if (status === 'present') return 'Présent';
  if (status === 'absent') return 'Absent';
  if (status === 'skipped') return 'Sauté';
  return 'En attente';
}

const History = () => {
  const {
    navigate,
    dateFilter,
    setDateFilter,
    routeFilter,
    setRouteFilter,
    busFilter,
    setBusFilter,
    trips,
    routes,
    buses,
    loading,
    selectedTrip,
    setSelectedTrip,
    tripDetails,
    loadingDetails,
    tripStats,
    tripPositions,
    tripMapStops,
    isGpsExpired,
    handleTripClick,
    resetFilters,
  } = useHistory();

  // student_checkin est un flag top-level (pas imbriqué sous une page dans la config), non
  // lisible via hasFeature/isFeatureEnabled — on vérifie donc directement le tier, comme le
  // fait le backend (règle n°1 CLAUDE.md : toute valeur ≠ 'premium' est traitée comme 'basic').
  const { currentSubscription } = useFeatureFlags();
  const showStudentStatus = currentSubscription === 'premium';

  const [mapOpen, setMapOpen] = useState(true);
  const [statsOpen, setStatsOpen] = useState(true);
  const [stopsOpen, setStopsOpen] = useState(true);

  const handleDialogOpen = (open) => {
    if (!open) setSelectedTrip(null);
    // Reset sections when dialog opens
    if (open) {
      setMapOpen(true);
      setStatsOpen(true);
      setStopsOpen(true);
    }
  };

  return (
    <div className="p-4 sm:p-6 space-y-6 bg-gray-50/50 min-h-screen">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin/tracking')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-gray-900">Historique des Trajets</h1>
            <p className="text-sm text-gray-500">Consultez et analysez les trajets passés</p>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div className="flex min-w-0 flex-wrap items-end gap-3">
            <div className="flex min-w-[10.5rem] flex-1 flex-col gap-1 sm:max-w-[14rem]">
              <label htmlFor="history-filter-date" className="text-xs font-medium text-gray-600">
                Date
              </label>
              <input
                id="history-filter-date"
                type="date"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm transition-all focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 [color-scheme:light]"
              />
            </div>
            <div className="flex min-w-[10.5rem] flex-1 flex-col gap-1 sm:max-w-[14rem]">
              <label htmlFor="history-filter-route" className="text-xs font-medium text-gray-600">
                Circuit
              </label>
              <select
                id="history-filter-route"
                value={routeFilter}
                onChange={(e) => setRouteFilter(e.target.value)}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm transition-all focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">Tous les circuits</option>
                {routes.map((route) => (
                  <option key={route.id} value={String(route.id)}>
                    {route.name || route.route_name || 'Circuit sans nom'}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex min-w-[10.5rem] flex-1 flex-col gap-1 sm:max-w-[14rem]">
              <label htmlFor="history-filter-bus" className="text-xs font-medium text-gray-600">
                Bus
              </label>
              <select
                id="history-filter-bus"
                value={busFilter}
                onChange={(e) => setBusFilter(e.target.value)}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm transition-all focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">Tous les bus</option>
                {buses.map((bus) => (
                  <option key={bus.id} value={String(bus.id)}>
                    {bus.bus_number}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex shrink-0 flex-col gap-1 self-end sm:self-auto">
            <span aria-hidden="true" className="invisible select-none text-xs font-medium text-gray-600">
              Date
            </span>
            <button
              type="button"
              className="whitespace-nowrap rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50"
              onClick={resetFilters}
            >
              Réinitialiser
            </button>
          </div>
        </div>
      </div>

      {/* ── Vue cartes (mobile < md) ── */}
      <div className="md:hidden divide-y divide-gray-100 rounded-xl border border-gray-200 bg-white overflow-hidden shadow-sm">
        {loading ? (
          <div className="h-24 flex items-center justify-center text-sm text-gray-500">Chargement...</div>
        ) : trips.length === 0 ? (
          <div className="h-24 flex items-center justify-center text-sm text-gray-500">
            Aucun trajet trouvé pour ces critères
          </div>
        ) : (
          trips.map((trip, index) => (
            <div
              key={trip.id || index}
              className="p-4 hover:bg-gray-50 active:bg-gray-100 cursor-pointer transition-colors"
              onClick={() => handleTripClick(trip)}
            >
              <div className="flex items-start justify-between gap-2 mb-2.5">
                <span className="font-semibold text-gray-900 text-sm">
                  {format(parseISO(trip.created_at), 'dd/MM/yyyy')}
                </span>
                <div className="flex items-center gap-1.5">
                  {getEndReasonBadge(trip.end_reason)}
                  {getStatusBadge(trip.status)}
                </div>
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center gap-2 min-w-0">
                  <Route className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                  <span className="text-sm font-medium text-gray-800 truncate">
                    {trip.routes?.route_name || 'Circuit inconnu'}
                  </span>
                  {trip.routes?.route_code && (
                    <span className="text-xs text-gray-400 font-mono shrink-0">{trip.routes.route_code}</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                  <span className="text-sm text-gray-600">{trip.buses?.bus_number || 'Sans bus'}</span>
                  {trip.drivers && (
                    <span className="text-xs text-gray-400 truncate">
                      · {trip.drivers.first_name} {trip.drivers.last_name}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <span className="flex items-center gap-1 text-green-600">
                    <Clock className="h-3 w-3" />
                    {trip.start_time ? format(parseISO(trip.start_time), 'HH:mm') : '-'}
                  </span>
                  <span className="text-gray-300">→</span>
                  <span className="flex items-center gap-1 text-red-500">
                    <Clock className="h-3 w-3" />
                    {trip.end_time ? format(parseISO(trip.end_time), 'HH:mm') : '-'}
                  </span>
                  <span className="text-gray-300">·</span>
                  <span className="text-gray-500">{calculateDuration(trip.start_time, trip.end_time)}</span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* ── Tableau (desktop ≥ md) ── */}
      <div className="hidden md:block">
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Circuit</TableHead>
                    <TableHead>Bus & Chauffeur</TableHead>
                    <TableHead>Horaires</TableHead>
                    <TableHead>Durée</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={7} className="h-24 text-center">
                        Chargement...
                      </TableCell>
                    </TableRow>
                  ) : trips.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="h-24 text-center text-gray-500">
                        Aucun trajet trouvé pour ces critères
                      </TableCell>
                    </TableRow>
                  ) : (
                    trips.map((trip, index) => (
                      <TableRow
                        key={trip.id || index}
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => handleTripClick(trip)}
                      >
                        <TableCell className="font-medium">{format(parseISO(trip.created_at), 'dd/MM/yyyy')}</TableCell>
                        <TableCell>
                          <div className="flex flex-col">
                            <span className="font-medium">{trip.routes?.route_name || 'Circuit inconnu'}</span>
                            <span className="text-xs text-gray-500">{trip.routes?.route_code}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col">
                            <span className="font-medium">{trip.buses?.bus_number || 'Sans bus'}</span>
                            <span className="text-xs text-gray-500">
                              {trip.drivers ? `${trip.drivers.first_name} ${trip.drivers.last_name}` : 'Sans chauffeur'}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col text-sm">
                            <span className="text-green-600 flex items-center gap-1">
                              <Clock className="h-3 w-3" />{' '}
                              {trip.start_time ? format(parseISO(trip.start_time), 'HH:mm') : '-'}
                            </span>
                            <span className="text-red-600 flex items-center gap-1">
                              <Clock className="h-3 w-3" />{' '}
                              {trip.end_time ? format(parseISO(trip.end_time), 'HH:mm') : '-'}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>{calculateDuration(trip.start_time, trip.end_time)}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1.5">
                            {getStatusBadge(trip.status)}
                            {getEndReasonBadge(trip.end_reason)}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleTripClick(trip);
                            }}
                          >
                            Détails
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>

      <Dialog open={!!selectedTrip} onOpenChange={handleDialogOpen}>
        <DialogContent className="!flex max-h-[92vh] min-h-0 max-w-5xl w-[95vw] flex-col gap-0 overflow-hidden p-0 sm:max-w-5xl">
          {/* En-tête */}
          <DialogHeader className="shrink-0 border-b px-6 py-4 text-left">
            <DialogTitle className="flex items-center gap-2 text-base">
              Détails du Trajet
              {getEndReasonBadge(selectedTrip?.end_reason)}
            </DialogTitle>
            <DialogDescription className="text-sm">
              {selectedTrip?.routes?.route_name}
              {selectedTrip && (
                <span className="ml-1 text-gray-400">
                  — {format(parseISO(selectedTrip.created_at), 'dd MMMM yyyy', { locale: fr })}
                  {selectedTrip.buses?.bus_number && ` · ${selectedTrip.buses.bus_number}`}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          {loadingDetails ? (
            <div className="flex flex-1 items-center justify-center p-12">
              <div className="text-sm text-gray-500">Chargement…</div>
            </div>
          ) : (
            <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
              {/* ── Section carte ─────────────────────────────────────────── */}
              <SectionToggle label="Carte du trajet" open={mapOpen} onToggle={() => setMapOpen((o) => !o)} />
              {mapOpen &&
                (isGpsExpired ? (
                  <div className="flex h-[340px] w-full flex-col items-center justify-center gap-2 rounded-lg bg-gray-100 text-gray-500">
                    <MapPin className="h-8 w-8 text-gray-300" />
                    <p className="text-sm font-medium">Données GPS non disponibles</p>
                    <p className="text-xs text-center px-8">
                      Les positions GPS sont conservées 30 jours pour votre offre.
                    </p>
                  </div>
                ) : tripPositions.length >= 2 ? (
                  <TripReplayMap positions={tripPositions} stops={tripMapStops} height="340px" />
                ) : (
                  <div className="flex h-36 w-full items-center justify-center gap-2 bg-gray-50 text-sm text-gray-400">
                    <MapPin className="h-4 w-4" />
                    Aucune donnée GPS disponible pour ce trajet
                  </div>
                ))}

              {/* ── Section statistiques ───────────────────────────────────── */}
              {tripStats && (
                <>
                  <SectionToggle label="Statistiques" open={statsOpen} onToggle={() => setStatsOpen((o) => !o)} />
                  {statsOpen && (
                    <div className="shrink-0 px-6 py-4">
                      <div className="grid grid-cols-3 gap-3">
                        <StatCard
                          icon={Route}
                          label="Distance"
                          value={`${tripStats.distanceKm} km`}
                          color="text-blue-600"
                        />
                        <StatCard
                          icon={Timer}
                          label="Durée"
                          value={`${tripStats.durationMin} min`}
                          color="text-purple-600"
                        />
                        <StatCard
                          icon={TrendingUp}
                          label="Arrêts visités"
                          value={`${tripStats.stopsVisited}/${tripStats.stopsCount}`}
                          color="text-green-600"
                        />
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* ── Section arrêts ─────────────────────────────────────────── */}
              <SectionToggle
                label={`Arrêts du trajet${tripDetails?.stops?.length ? ` (${tripDetails.stops.length})` : ''}`}
                open={stopsOpen}
                onToggle={() => setStopsOpen((o) => !o)}
              />
              {stopsOpen &&
                (tripDetails ? (
                  <div className="flex-1 px-6 py-4">
                    <div className="space-y-0">
                      {(tripDetails.stops || []).map((stop, index, arr) => {
                        const timeLabel = getStopTime(stop);
                        const statusColor = getStopStatusColor(stop.status);
                        const statusLabel = getStopStatusLabel(stop.status);
                        const isSkipped = stop.status === 'skipped';

                        return (
                          <div key={stop.id || index} className="relative flex gap-4 pb-4 last:pb-0">
                            {index !== arr.length - 1 && (
                              <div className="absolute bottom-0 left-[11px] top-6 w-0.5 bg-gray-200" />
                            )}
                            {/* Bulle numérotée */}
                            <div
                              className="relative z-10 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white"
                              style={{ background: statusColor }}
                            >
                              {index + 1}
                            </div>

                            {/* Contenu */}
                            <div className="min-w-0 flex-1 rounded-lg border border-gray-100 bg-white px-3 py-2 shadow-sm">
                              <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-0.5">
                                <h4
                                  className={`font-medium ${isSkipped ? 'text-gray-400 line-through' : 'text-gray-900'}`}
                                >
                                  {stop.stop_name || stop.stopName || stop.name || 'Arrêt inconnu'}
                                </h4>
                                <div className="flex items-center gap-2 shrink-0">
                                  {timeLabel && (
                                    <span className="flex items-center gap-1 font-mono text-xs text-gray-500">
                                      <Clock className="h-3 w-3" />
                                      {timeLabel}
                                    </span>
                                  )}
                                  <span
                                    className="rounded-full px-2 py-0.5 text-xs font-medium"
                                    style={{ background: `${statusColor}20`, color: statusColor }}
                                  >
                                    {statusLabel}
                                  </span>
                                </div>
                              </div>

                              {stop.students && stop.students.length > 0 && (
                                <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 border-t border-gray-100 pt-1.5">
                                  {stop.students.map((student, sIndex) => {
                                    const name = `${student.firstName || student.first_name} ${
                                      student.lastName || student.last_name
                                    }`;
                                    // Reflet passif du tap parent sur l'écran de suivi live — signal
                                    // purement observationnel, indépendant de `status` (ne suit donc
                                    // pas le gate Premium ci-dessus : fiable dans les deux tiers).
                                    // Tap/clic pour afficher (pas seulement hover — nécessaire sur
                                    // tablette, voir ParentConfirmedTick).
                                    const parentConfirmedIcon = (
                                      <ParentConfirmedTick confirmedAt={student.parentConfirmedAt} />
                                    );

                                    if (!showStudentStatus) {
                                      // Basic : liste des élèves seule, pas de statut individuel
                                      // (pas d'app chauffeur pour le confirmer réellement).
                                      return (
                                        <span
                                          key={student.id || sIndex}
                                          className="inline-flex items-center gap-1 text-xs text-gray-500"
                                        >
                                          {name}
                                          {parentConfirmedIcon}
                                        </span>
                                      );
                                    }
                                    const sColor = getStudentStatusColor(student.status);
                                    const sLabel = getStudentStatusLabel(student.status);
                                    return (
                                      <span
                                        key={student.id || sIndex}
                                        title={sLabel}
                                        className="inline-flex items-center gap-1.5 text-xs text-gray-600"
                                      >
                                        <span
                                          className="h-1.5 w-1.5 shrink-0 rounded-full"
                                          style={{ background: sColor }}
                                        />
                                        {name}
                                        <span className="text-[10px]" style={{ color: sColor }}>
                                          · {sLabel}
                                        </span>
                                        {parentConfirmedIcon}
                                      </span>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                      {(!tripDetails.stops || tripDetails.stops.length === 0) && (
                        <p className="py-4 text-center text-sm text-gray-400">Aucun arrêt enregistré</p>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="py-8 text-center text-sm text-gray-400">Aucun détail disponible</div>
                ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default History;
